'use client';
import type React from 'react';
import { cloneElement, createContext, useCallback, useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { v4 as uuid } from 'uuid';
import { useClientOnlyConstant } from '../../hooks/useClientOnlyConstant';
import {
  type BaseElementEvent,
  type ElementInteractedPayload,
  type ElementViewedPayload,
  EventName,
} from '../../Shibuya';
import { useAnalytics } from '..';
import { useAnalyticsPageView } from '../AnalyticsPageView';
import { getCleanLocationHref } from '../urlUtils';

export interface ElementViewedExtra {
  /** Overrides the wrapper's default `entity_name` for this single event, for sub-elements of a
   * shared container (e.g. a footer's own view vs. a thumbnail interaction within it). */
  entityName?: string;
  itemPosition?: BaseElementEvent['item_position'];
  itemCopy?: BaseElementEvent['item_copy'];
  additionalAttributes?: BaseElementEvent['additional_attributes'];
}

export interface ElementViewedContextValue {
  eventId: string;
  fireElementInteracted: (interactionType: string, extra?: ElementViewedExtra) => void;
}

const ElementViewedContext = createContext<ElementViewedContextValue | null>(null);

export interface ElementViewedProps {
  entityName: string;
  sectionName: string;
  itemPosition?: BaseElementEvent['item_position'];
  itemCopy?: BaseElementEvent['item_copy'];
  additionalAttributes?: BaseElementEvent['additional_attributes'];
  children?: React.JSX.Element | ((context: ElementViewedContextValue) => React.JSX.Element);
}

/**
 * Fires a paired `Element Viewed` / `Element Interacted` sequence for one UI element -- mirrors
 * `AnalyticsSkuViewed`'s own intersection-observer + context pattern. `Element Viewed` mints
 * `event_id` once and fires it once the wrapped child actually enters the viewport (not merely on
 * mount); `fireElementInteracted` (exposed via context) mints a fresh id per call and threads the
 * view's id through as `parent_event_id`.
 */
export const ElementViewed = ({
  children,
  entityName,
  sectionName,
  itemPosition,
  itemCopy,
  additionalAttributes,
}: ElementViewedProps) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const { pageViewId, pageName, pageViewReady, pageType } = useAnalyticsPageView();
  const eventId = useClientOnlyConstant(() => uuid(), '');
  const { ref: inViewRef, inView } = useInView({
    root: null,
    rootMargin: '1px 0px 0px 0px',
    threshold: 0,
    triggerOnce: true,
  });

  const viewed = pageViewReady && !!deviceId && inView;

  useEffect(() => {
    if (!viewed) return;
    const payload: ElementViewedPayload = {
      deployable_name: deployableName,
      deployable_version: deployableVersion,
      event_id: eventId,
      entity_name: entityName,
      section_name: sectionName,
      pageview_id: pageViewId,
      page_name: pageName,
      page_url: getCleanLocationHref(),
      item_position: itemPosition,
      item_copy: itemCopy,
      additional_attributes: {
        traditional_device_id: deviceId as string,
        ...(pageType && { page_type: pageType }),
        ...additionalAttributes,
      },
    };
    analytics?.track(EventName.ELEMENT_VIEWED, payload, {
      context: { app: { name: deployableName, version: gitSha } },
    });
  }, [viewed]);

  const fireElementInteracted = useCallback(
    (interactionType: string, extra: ElementViewedExtra = {}) => {
      if (!pageViewReady || !deviceId) return;
      const payload: ElementInteractedPayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        event_id: uuid(),
        parent_event_id: eventId,
        entity_name: extra.entityName ?? entityName,
        section_name: sectionName,
        interaction_type: interactionType,
        pageview_id: pageViewId,
        page_name: pageName,
        page_url: getCleanLocationHref(),
        item_position: extra.itemPosition ?? itemPosition,
        item_copy: extra.itemCopy ?? itemCopy,
        additional_attributes: {
          traditional_device_id: deviceId,
          ...(pageType && { page_type: pageType }),
          ...extra.additionalAttributes,
        },
      };
      analytics?.track(EventName.ELEMENT_INTERACTED, payload, {
        context: { app: { name: deployableName, version: gitSha } },
      });
    },
    [
      analytics,
      deviceId,
      deployableName,
      deployableVersion,
      gitSha,
      eventId,
      viewed,
      entityName,
      sectionName,
      pageType,
      itemPosition,
      itemCopy,
      pageViewId,
      pageName,
      pageViewReady,
    ],
  );

  const context: ElementViewedContextValue = { eventId, fireElementInteracted };
  const child = typeof children === 'function' ? children(context) : children;
  if (!child) {
    throw new Error('Missing Child');
  }
  return (
    <ElementViewedContext.Provider value={context}>
      {cloneElement(child, { ref: inViewRef })}
    </ElementViewedContext.Provider>
  );
};

export const useElementViewedContext = () => {
  const context = useContext(ElementViewedContext);
  if (!context) {
    throw new Error('useElementViewedContext must be used within ElementViewed');
  }
  return context;
};
